/*
 * Vinelora theme — core behaviour.
 * Cart (Shopify AJAX Cart API), cart drawer, reservation countdown, quick add,
 * carousels, before/after slider and the sticky offer bar.
 * Talks only to this store's own /cart endpoints. No external requests.
 */
(() => {
  const root = (window.theme && window.theme.routes && window.theme.routes.root) || '/';
  const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };

  /* ---------- Money ---------- */
  const groupDigits = (value, separator) => value.replace(/\B(?=(\d{3})+(?!\d))/g, separator);

  const formatMoney = (cents, format) => {
    const moneyFormat = format || (window.theme && window.theme.moneyFormat) || '${{amount}}';
    const [whole, decimals] = (Number(cents) / 100).toFixed(2).split('.');
    return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return groupDigits(whole, ',');
        case 'amount_with_comma_separator':
          return `${groupDigits(whole, '.')},${decimals}`;
        case 'amount_no_decimals_with_comma_separator':
          return groupDigits(whole, '.');
        case 'amount_with_apostrophe_separator':
          return `${groupDigits(whole, "'")}.${decimals}`;
        default:
          return `${groupDigits(whole, ',')}.${decimals}`;
      }
    });
  };

  /* ---------- Cart reservation countdown ---------- */
  const TIMER_KEY = 'vinelora-cart-timer-end';

  const Timer = {
    minutes() {
      const el = document.querySelector('[data-cart-timer]');
      return (el && Number(el.dataset.minutes)) || 10;
    },
    readEnd() {
      try {
        return Number(sessionStorage.getItem(TIMER_KEY)) || 0;
      } catch (error) {
        return 0;
      }
    },
    writeEnd(value) {
      try {
        sessionStorage.setItem(TIMER_KEY, String(value));
      } catch (error) {
        /* storage unavailable (private mode) — countdown still renders */
      }
    },
    start() {
      if (Timer.readEnd() < Date.now()) Timer.writeEnd(Date.now() + Timer.minutes() * 60000);
    },
    render() {
      const outputs = document.querySelectorAll('[data-cart-timer-value]');
      if (!outputs.length) return;
      let end = Timer.readEnd();
      if (end < Date.now()) {
        end = Date.now() + Timer.minutes() * 60000;
        Timer.writeEnd(end);
      }
      const left = Math.max(0, end - Date.now());
      const minutes = String(Math.floor(left / 60000)).padStart(2, '0');
      const seconds = String(Math.floor(left / 1000) % 60).padStart(2, '0');
      outputs.forEach((el) => {
        el.textContent = `${minutes}:${seconds}`;
      });
    },
  };

  /* ---------- Cart drawer ---------- */
  // The wallet buttons (Shop Pay / Apple Pay / Google Pay) ship as <script src> inside the
  // cart markup. A drawer re-render builds that markup with DOMParser, which never executes
  // scripts, so load any we do not already have — matched by src, so nothing runs twice.
  const hydrateScripts = (node) => {
    node.querySelectorAll('script[src]').forEach((script) => {
      if (document.querySelector(`script[src="${script.src}"]`)) return;
      const loader = document.createElement('script');
      Array.from(script.attributes).forEach((attr) => loader.setAttribute(attr.name, attr.value));
      document.head.appendChild(loader);
    });
  };

  const Drawer = {
    el: () => document.querySelector('[data-cart-drawer]'),
    open() {
      const drawer = Drawer.el();
      if (!drawer) return;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('scroll-lock');
      const close = drawer.querySelector('[data-cart-drawer-close]');
      if (close) close.focus({ preventScroll: true });
    },
    close() {
      const drawer = Drawer.el();
      if (!drawer || !drawer.classList.contains('is-open')) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('scroll-lock');
    },
  };

  /* ---------- Add-on rules ---------- */
  // Add-on products (e.g. Mystery Gift, Shipping Protection) may only be in the cart together with
  // one of the main products chosen in Theme settings → Cart add-on rules.
  const rules = (window.theme && window.theme.cartRules) || {};
  const toIdSet = (list) => new Set((list || []).map(Number));
  const addonProductIds = toIdSet(rules.addonProductIds);
  const addonVariantIds = toIdSet(rules.addonVariantIds);
  const requiredProductIds = toIdSet(rules.requiredProductIds);
  const requiredVariantIds = toIdSet(rules.requiredVariantIds);
  const addonRulesActive = addonProductIds.size > 0 && requiredProductIds.size > 0;
  const requiredTitle = rules.requiredTitle || 'a main product';
  // A $0 gift (e.g. Free Mystery Gift with the Buy 3 bundle) needs enough main products; one per order.
  const freeGiftProductId = Number(rules.freeGiftProductId) || 0;
  const freeGiftVariantIds = toIdSet(rules.freeGiftVariantIds);
  const freeGiftTitle = rules.freeGiftTitle || 'The free gift';
  const freeGiftMin = Number(rules.freeGiftMinQuantity) || 3;
  const giftRulesActive = freeGiftProductId > 0 && requiredProductIds.size > 0;
  const rulesActive = addonRulesActive || giftRulesActive;
  const NOTICE_KEY = 'vinelora-addons-removed';

  const Notice = {
    remember(message) {
      try {
        sessionStorage.setItem(NOTICE_KEY, message);
      } catch (error) {
        /* storage unavailable — the cart is still corrected */
      }
    },
    // Shows (once) the message about removed add-ons right after the given element.
    showAfter(anchor) {
      let message = null;
      try {
        message = sessionStorage.getItem(NOTICE_KEY);
        sessionStorage.removeItem(NOTICE_KEY);
      } catch (error) {
        return;
      }
      if (!message || !anchor) return;
      const notice = document.createElement('p');
      notice.className = 'cart-notice';
      notice.setAttribute('role', 'status');
      notice.textContent = message;
      anchor.after(notice);
    },
  };

  const AddonRules = {
    hasMainProduct: (cart) => cart.items.some((item) => requiredProductIds.has(item.product_id)),

    mainQuantity: (cart) =>
      cart.items.filter((item) => requiredProductIds.has(item.product_id)).reduce((sum, item) => sum + item.quantity, 0),

    orphanedLines(cart) {
      if (!addonRulesActive || AddonRules.hasMainProduct(cart)) return [];
      return cart.items.filter((item) => addonProductIds.has(item.product_id));
    },

    // Line quantity changes needed to follow the rules, plus a message for the shopper.
    corrections(cart) {
      const updates = {};
      const messages = [];

      const orphans = AddonRules.orphanedLines(cart);
      if (orphans.length) {
        orphans.forEach((item) => {
          updates[item.key] = 0;
        });
        const names = [...new Set(orphans.map((item) => item.product_title))].join(' and ');
        messages.push(`${names} ${orphans.length > 1 ? 'were' : 'was'} removed from your cart — ${orphans.length > 1 ? 'they' : 'it'} can only be ordered together with ${requiredTitle}.`);
      }

      // A free bundle add-on (e.g. Priority Processing with Buy 2) only stays while the order still has
      // enough main products for that bundle; otherwise it would be charged at full price.
      if (requiredProductIds.size) {
        const bundleGifts = cart.items.filter(
          (item) => item.properties && item.properties._bundle_gift && updates[item.key] === undefined
        );
        const mainQty = AddonRules.mainQuantity(cart);
        const dropped = bundleGifts.filter((item) => mainQty < (Number(item.properties._bundle_gift) || 2));
        dropped.forEach((item) => {
          updates[item.key] = 0;
        });
        if (dropped.length) {
          const names = [...new Set(dropped.map((item) => item.product_title))].join(' and ');
          messages.push(`${names} was removed — it's only included free with ${Number(dropped[0].properties._bundle_gift) || 2} or more ${requiredTitle}.`);
        }
      }

      // Add-ons are one per order: quantity 1, and only one option per product (e.g. a single tip).
      if (addonRulesActive && !orphans.length) {
        const seen = new Set();
        let trimmed = false;
        cart.items.forEach((item) => {
          if (!addonProductIds.has(item.product_id) || updates[item.key] === 0) return;
          if (seen.has(item.product_id)) {
            updates[item.key] = 0;
            trimmed = true;
          } else {
            seen.add(item.product_id);
            if (item.quantity > 1) {
              updates[item.key] = 1;
              trimmed = true;
            }
          }
        });
        if (trimmed) messages.push('Add-ons are limited to one of each per order.');
      }

      const giftLines = giftRulesActive ? cart.items.filter((item) => item.product_id === freeGiftProductId) : [];
      if (giftLines.length && AddonRules.mainQuantity(cart) < freeGiftMin) {
        giftLines.forEach((item) => {
          updates[item.key] = 0;
        });
        messages.push(`${freeGiftTitle} was removed from your cart — it's only included with ${freeGiftMin} or more ${requiredTitle}.`);
      } else if (giftLines.reduce((sum, item) => sum + item.quantity, 0) > 1) {
        let left = 1;
        giftLines.forEach((item) => {
          const keep = Math.min(left, item.quantity);
          left -= keep;
          if (keep !== item.quantity) updates[item.key] = keep;
        });
        messages.push(`Only one ${freeGiftTitle} is included per order.`);
      }

      return { updates, message: messages.join(' ') };
    },

    // Removes add-ons left without a main product and keeps the free gift within its rules.
    // Resolves with the up-to-date cart.
    async enforce(cart) {
      const { updates, message } = AddonRules.corrections(cart);
      if (!Object.keys(updates).length) return { cart, removed: false };

      const response = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({ updates }),
      });
      const updated = await response.json();
      Notice.remember(message);
      return { cart: updated, removed: true };
    },

    // Blocks adding add-ons on their own, and the free gift without enough main products.
    assertCanAdd(items, cart) {
      if (!rulesActive) return;
      const ids = items.map((item) => Number(item.id));
      const includesMain = ids.some((id) => requiredVariantIds.has(id));

      if (giftRulesActive && ids.some((id) => freeGiftVariantIds.has(id))) {
        const addedMain = items
          .filter((item) => requiredVariantIds.has(Number(item.id)))
          .reduce((sum, item) => sum + (Number(item.quantity) || 1), 0);
        if (AddonRules.mainQuantity(cart) + addedMain < freeGiftMin) {
          throw new Error(`${freeGiftTitle} is only included with ${freeGiftMin} or more ${requiredTitle}.`);
        }
      }

      if (!addonRulesActive) return;
      const onlyAddons = ids.length > 0 && ids.every((id) => addonVariantIds.has(id));
      if (!onlyAddons || includesMain || AddonRules.hasMainProduct(cart)) return;
      throw new Error(`Please add ${requiredTitle} to your cart first — this add-on can only be ordered together with it.`);
    },
  };

  /* ---------- Cart API ---------- */
  const Cart = {
    async get() {
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      return response.json();
    },

    async add(items, { open = true } = {}) {
      if (rulesActive) AddonRules.assertCanAdd(items, await Cart.get());
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({ items }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'This item could not be added to your cart.');
      Timer.start();
      const cart = await Cart.refresh();
      // Fired only after a successful add. A listener (the upsell popup) may call preventDefault()
      // to hold the cart back and show it itself later, so the cart never opens twice.
      const added = new CustomEvent('vinelora:item-added', {
        cancelable: true,
        detail: { items: data.items || [], cart },
      });
      document.dispatchEvent(added);
      if (open && !added.defaultPrevented) Cart.show();
      return data;
    },

    show() {
      if (Drawer.el()) {
        Drawer.open();
      } else {
        window.location.href = `${root}cart`;
      }
    },

    async change(key, quantity) {
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({ id: key, quantity }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'Your cart could not be updated.');
      await Cart.refresh();
      return data;
    },

    async refresh({ open = false } = {}) {
      // Every add, quantity change and removal ends here, so orphaned add-ons are cleaned up in one place.
      const { cart } = await AddonRules.enforce(await Cart.get());

      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });

      const drawer = Drawer.el();
      if (drawer) {
        const response = await fetch(`${root}?sections=cart-drawer`);
        const sections = await response.json();
        const doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        const fresh = doc.querySelector('[data-cart-drawer-inner]');
        const current = drawer.querySelector('[data-cart-drawer-inner]');
        if (fresh && current) {
          current.replaceWith(fresh);
          hydrateScripts(fresh);
        }
        Notice.showAfter(drawer.querySelector('[data-cart-drawer-inner] .cart-drawer__header'));
        Timer.render();
        Discount.show();
        if (open) Drawer.open();
      } else if (open) {
        window.location.href = `${root}cart`;
      }

      // The cart page shows the same lines, so every change re-renders it in place —
      // no "Update cart" button, and the quantity on screen is always the cart's own.
      const page = document.querySelector('[data-cart-page]');
      if (page && page.dataset.sectionId) {
        try {
          const response = await fetch(`${root}cart?section_id=${encodeURIComponent(page.dataset.sectionId)}`);
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const fresh = doc.querySelector('[data-cart-page]');
          if (fresh) {
            page.replaceWith(fresh);
            Notice.showAfter(fresh.querySelector('[data-cart-notice-anchor]'));
            Discount.show(fresh);
          }
        } catch (error) {
          // The cart itself did change, so fall back to a plain reload rather than lying on screen.
          window.location.reload();
        }
      }

      document.dispatchEvent(new CustomEvent('vinelora:cart-updated', { detail: { cart } }));
      return cart;
    },
  };

  /* ---------- Carousels ---------- */
  const initCarousel = (carousel) => {
    if (carousel.dataset.carouselReady) return;
    const track = carousel.querySelector('[data-carousel-track]');
    if (!track || !track.children.length) return;
    carousel.dataset.carouselReady = 'true';

    const slides = Array.from(track.children);
    const dotsWrap = carousel.querySelector('[data-carousel-dots]');
    const autoplaySeconds = Number(carousel.dataset.autoplay) || 0;
    let timer = null;

    const currentIndex = () => {
      let index = 0;
      let best = Infinity;
      slides.forEach((slide, i) => {
        const distance = Math.abs(slide.offsetLeft - track.scrollLeft);
        if (distance < best) {
          best = distance;
          index = i;
        }
      });
      return index;
    };

    const goTo = (index) => {
      const target = slides[(index + slides.length) % slides.length];
      track.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
    };

    let dots = [];
    if (dotsWrap && slides.length > 1) {
      dotsWrap.innerHTML = '';
      dots = slides.map((slide, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'carousel__dot';
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
        dot.addEventListener('click', () => goTo(i));
        dotsWrap.appendChild(dot);
        return dot;
      });
    }

    const updateDots = () => {
      const active = currentIndex();
      dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === active)));
    };

    let scrollFrame;
    track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(scrollFrame);
        scrollFrame = requestAnimationFrame(updateDots);
      },
      { passive: true }
    );

    // When several cards show at once the track can't scroll to the last few, so wrap at the edges.
    const atEnd = () => track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
    const atStart = () => track.scrollLeft <= 4;
    carousel.querySelectorAll('[data-carousel-prev]').forEach((btn) =>
      btn.addEventListener('click', () => (atStart() ? track.scrollTo({ left: track.scrollWidth, behavior: 'smooth' }) : goTo(currentIndex() - 1)))
    );
    carousel.querySelectorAll('[data-carousel-next]').forEach((btn) =>
      btn.addEventListener('click', () => (atEnd() ? goTo(0) : goTo(currentIndex() + 1)))
    );

    const stop = () => clearInterval(timer);
    const play = () => {
      stop();
      if (autoplaySeconds > 0 && slides.length > 1) {
        timer = setInterval(() => goTo(currentIndex() + 1), autoplaySeconds * 1000);
      }
    };
    carousel.addEventListener('mouseenter', stop);
    carousel.addEventListener('mouseleave', play);
    carousel.addEventListener('touchstart', stop, { passive: true });

    updateDots();
    play();
  };

  /* ---------- Before / after slider ---------- */
  const initBeforeAfter = (el) => {
    const input = el.querySelector('input[type="range"]');
    if (!input || el.dataset.beforeAfterReady) return;
    el.dataset.beforeAfterReady = 'true';
    const update = () => el.style.setProperty('--position', `${input.value}%`);
    input.addEventListener('input', update);
    update();
  };

  /* ---------- Sticky offer bar ---------- */
  const initStickyOffer = () => {
    const bar = document.querySelector('[data-sticky-offer]');
    const trigger = document.querySelector('[data-sticky-trigger]');
    if (!bar || !trigger || bar.dataset.stickyReady) return;
    bar.dataset.stickyReady = 'true';
    new IntersectionObserver(([entry]) => {
      const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', scrolledPast);
      bar.setAttribute('aria-hidden', String(!scrolledPast));
    }).observe(trigger);
  };

  /* ---------- Events ---------- */
  const reportError = (error) => {
    // Surface cart errors to the shopper rather than failing silently.
    window.alert(error.message);
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-cart-drawer-open]');
    if (opener && Drawer.el()) {
      event.preventDefault();
      Drawer.open();
      return;
    }

    if (event.target.closest('[data-cart-drawer-close]')) {
      event.preventDefault();
      Drawer.close();
      return;
    }

    const upsellToggle = event.target.closest('[data-cart-upsell-toggle]');
    if (upsellToggle) {
      event.preventDefault();
      const variantId = Number(upsellToggle.dataset.variantId);
      if (!variantId) return;
      // Flip the switch first, then add: the drawer redraws without this row once the
      // add-on is a cart line, so the shopper sees the switch travel before it goes.
      upsellToggle.setAttribute('aria-checked', 'true');
      upsellToggle.setAttribute('aria-busy', 'true');
      window.setTimeout(() => {
        Cart.add([{ id: variantId, quantity: 1 }], { open: false }).catch((error) => {
          upsellToggle.setAttribute('aria-checked', 'false');
          upsellToggle.removeAttribute('aria-busy');
          reportError(error);
        });
      }, 220);
      return;
    }

    const quantityButton = event.target.closest('[data-cart-quantity]');
    if (quantityButton) {
      event.preventDefault();
      const item = quantityButton.closest('[data-cart-item]');
      if (item) item.classList.add('is-loading');
      Cart.change(item.dataset.key, Number(quantityButton.dataset.cartQuantity)).catch((error) => {
        if (item) item.classList.remove('is-loading');
        reportError(error);
      });
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') Drawer.close();
  });

  // Collection filters & sorting apply as soon as a control changes.
  document.addEventListener('change', (event) => {
    const control = event.target.closest('[data-auto-submit]');
    if (control && control.form) control.form.requestSubmit();
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-quick-add]');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    if (button) button.setAttribute('aria-busy', 'true');
    const data = new FormData(form);
    try {
      await Cart.add([{ id: Number(data.get('id')), quantity: Number(data.get('quantity')) || 1 }]);
    } catch (error) {
      reportError(error);
    } finally {
      if (button) button.removeAttribute('aria-busy');
    }
  });

  /* ---------- Add-on rules: page load & checkout ---------- */
  const isCartPage = () => Boolean(document.querySelector('[data-cart-page]'));

  // Carts can change outside this page (other tabs, the cart page form, direct links), so check on load.
  const enforceOnLoad = async () => {
    if (!rulesActive) return;
    try {
      const { removed } = await AddonRules.enforce(await Cart.get());
      if (removed && isCartPage()) {
        window.location.reload();
        return;
      }
      if (removed) {
        await Cart.refresh();
        return;
      }
      Notice.showAfter(document.querySelector('[data-cart-notice-anchor]'));
    } catch (error) {
      /* network hiccup — the next cart action runs the check again */
    }
  };

  /* ---------- Discount code ---------- */
  // Shopify prices the code on the cart too, so the total updates here and the code is
  // checked before we tell the shopper it worked. /cart.js reports each code as applicable or not.
  const DISCOUNT_KEY = 'ringbrella-discount-code';

  const Discount = {
    read() {
      try {
        return sessionStorage.getItem(DISCOUNT_KEY) || '';
      } catch (error) {
        return '';
      }
    },
    write(code) {
      try {
        if (code) sessionStorage.setItem(DISCOUNT_KEY, code);
        else sessionStorage.removeItem(DISCOUNT_KEY);
      } catch (error) {
        /* storage unavailable (private mode) — the code still holds for this page view */
      }
    },
    // Used instead of a plain /checkout link, because the cart form submit is intercepted below.
    checkoutUrl() {
      const code = Discount.read();
      return code ? `${root}checkout?discount=${encodeURIComponent(code)}` : `${root}checkout`;
    },
    // Hands the code to Shopify, which prices it into the cart. Reports whether the request
    // itself went through, so a refusal is never shown to the shopper as a working code.
    async register(code) {
      try {
        const response = await fetch(`${root}discount/${encodeURIComponent(code)}`);
        return response.ok;
      } catch (error) {
        return false;
      }
    },
    // null when the check itself failed, so a network hiccup never reads as a bad code.
    async check(code) {
      try {
        const cart = await Cart.get();
        // An empty bag carries no discount lines yet, so there is nothing to verify. Report
        // "unknown" rather than "bad code", or a code applied from the product page before
        // anything is added would wrongly read as invalid.
        if (!cart.item_count) return null;
        const entry = (cart.discount_codes || []).find((d) => (d.code || '').toUpperCase() === code);
        return entry ? entry.applicable === true : false;
      } catch (error) {
        return null;
      }
    },
    state(status, text) {
      document.querySelectorAll('[data-cart-discount]').forEach((wrap) => {
        const message = wrap.querySelector('[data-cart-discount-message]');
        wrap.classList.toggle('is-applied', status === 'applied');
        wrap.classList.toggle('is-error', status === 'error');
        wrap.classList.toggle('is-busy', status === 'busy');
        if (!message) return;
        message.textContent = text || '';
        message.hidden = !text;
      });
    },
    show(scope = document) {
      const code = Discount.read();
      scope.querySelectorAll('[data-cart-discount]').forEach((wrap) => {
        const input = wrap.querySelector('[data-cart-discount-input]');
        if (input && code) input.value = code;
      });
      if (code) Discount.state('applied', `${code} applied`);
    },
    async apply(code) {
      const clean = (code || '').trim().toUpperCase();
      if (!clean) return;
      document.querySelectorAll('[data-cart-discount-input]').forEach((input) => {
        input.value = clean;
      });
      Discount.state('busy', 'Checking…');

      const registered = await Discount.register(clean);
      const ok = await Discount.check(clean);

      // ok === null means an empty bag, where there is no cart line to verify against, so
      // whether the request was accepted is the only evidence there is.
      if (ok === false || (ok === null && !registered)) {
        Discount.write('');
        Discount.state('error', `${clean} is not a valid code`);
        return;
      }

      Discount.write(clean);
      // Re-prices the cart, so the subtotal shown matches what the shopper will pay.
      await Cart.refresh();
      Discount.state('applied', ok === null ? `${clean} applied at checkout` : `${clean} applied`);
    },
    init() {
      // A code can arrive from a link, e.g. an email: /cart?discount=COMEBACK05
      const fromUrl = new URLSearchParams(window.location.search).get('discount');
      if (fromUrl) {
        Discount.apply(fromUrl);
        return;
      }
      Discount.show();
    },
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-cart-discount-apply]');
    if (!button) return;
    const wrap = button.closest('[data-cart-discount]');
    const input = wrap && wrap.querySelector('[data-cart-discount-input]');
    if (input) Discount.apply(input.value);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' || !event.target.closest('[data-cart-discount-input]')) return;
    event.preventDefault();
    Discount.apply(event.target.value);
  });

  // Last check before checkout, for both the cart drawer and the cart page.
  document.addEventListener('submit', async (event) => {
    const submitter = event.submitter;
    if (!rulesActive || !submitter || submitter.name !== 'checkout') return;
    event.preventDefault();
    submitter.setAttribute('aria-busy', 'true');
    try {
      const form = event.target;
      // The cart page form may carry quantity edits that haven't been saved yet.
      if (form.querySelector('[name="updates[]"]')) {
        await fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });
      }
      const { cart, removed } = await AddonRules.enforce(await Cart.get());
      if (removed || cart.item_count === 0) {
        if (isCartPage()) {
          window.location.reload();
        } else {
          await Cart.refresh({ open: true });
        }
        return;
      }
      window.location.href = Discount.checkoutUrl();
    } catch (error) {
      window.location.href = Discount.checkoutUrl();
    } finally {
      submitter.removeAttribute('aria-busy');
    }
  });

  /* ---------- FAQ: one answer open at a time ---------- */
  // 'toggle' does not bubble, so this listens in the capture phase.
  document.addEventListener(
    'toggle',
    (event) => {
      const item = event.target;
      if (!item.matches || !item.matches('details.faq__item') || !item.open) return;
      const scope = item.closest('.faq, [data-faq]') || document;
      scope.querySelectorAll('details.faq__item[open]').forEach((other) => {
        if (other !== item) other.open = false;
      });
    },
    true
  );

  /* ---------- Trust badges and delivery timeline: one reveal in full view ---------- */
  // The hidden state is added by script, so the row stays visible if this never runs.
  const initBadgeReveal = (scope = document) => {
    scope.querySelectorAll('.trust-badges, .delivery-timeline').forEach((list) => {
      if (list.dataset.revealReady) return;
      list.dataset.revealReady = 'true';
      list.classList.add('is-animated');

      let played = false;

      // The run waits until the whole row is on screen, so the shopper never catches half
      // of it animating, and then holds still however long they keep scrolling past it.
      // It arms again only once the row has left the viewport completely, so coming back
      // to it plays the reveal afresh. 0.95 rather than 1: a row sitting flush against the
      // viewport edge reports a hair under full.
      // A row taller than the screen can never be fully on it, so it settles for a good
      // part of itself — otherwise it would stay hidden for good.
      const needed = (entry) =>
        entry.boundingClientRect.height > (window.innerHeight || 0) * 0.9 ? 0.4 : 0.95;

      new IntersectionObserver(
        ([entry]) => {
          if (entry.intersectionRatio >= needed(entry)) {
            if (played) return;
            played = true;
            list.classList.add('is-in-view');
          } else if (!entry.isIntersecting) {
            played = false;
            list.classList.remove('is-in-view');
          }
        },
        { threshold: [0, 0.4, 0.95, 1] }
      ).observe(list);
    });
  };

  /* ---------- Gallery zoom ---------- */
  // Tapping a product photo opens it full screen, where the phone's own pinch-zoom takes over.
  const initGalleryZoom = (scope = document) => {
    const gallery = scope.querySelector('[data-product-gallery]');
    if (!gallery || gallery.dataset.zoomReady) return;
    gallery.dataset.zoomReady = 'true';

    const images = Array.from(gallery.querySelectorAll('.product__slide img'));
    if (!images.length) return;

    // Shopify serves whatever width the layout asked for; the overlay wants the large one.
    const bigSrc = (img) => (img.currentSrc || img.src).replace(/([?&]width=)\d+/, '$11600');

    let overlay = null;
    let index = 0;

    const show = (i) => {
      index = (i + images.length) % images.length;
      overlay.querySelector('[data-zoom-img]').src = bigSrc(images[index]);
      overlay.querySelector('[data-zoom-count]').textContent = index + 1 + ' / ' + images.length;
    };

    const close = () => {
      if (!overlay) return;
      overlay.classList.remove('is-open');
      document.documentElement.classList.remove('scroll-lock');
      window.setTimeout(() => {
        if (overlay) overlay.hidden = true;
      }, 200);
    };

    const build = () => {
      overlay = document.createElement('div');
      overlay.className = 'zoom-overlay';
      overlay.hidden = true;
      overlay.innerHTML =
        '<button type="button" class="zoom-overlay__close" data-zoom-close aria-label="Close">&times;</button>' +
        '<button type="button" class="zoom-overlay__nav zoom-overlay__nav--prev" data-zoom-prev aria-label="Previous image">&#8249;</button>' +
        '<img class="zoom-overlay__img" data-zoom-img alt="">' +
        '<button type="button" class="zoom-overlay__nav zoom-overlay__nav--next" data-zoom-next aria-label="Next image">&#8250;</button>' +
        '<p class="zoom-overlay__count" data-zoom-count></p>';
      document.body.appendChild(overlay);

      overlay.addEventListener('click', (event) => {
        if (event.target.closest('[data-zoom-close]') || event.target === overlay) close();
        if (event.target.closest('[data-zoom-prev]')) show(index - 1);
        if (event.target.closest('[data-zoom-next]')) show(index + 1);
      });

      let startX = 0;
      overlay.addEventListener('touchstart', (event) => {
        startX = event.changedTouches[0].clientX;
      }, { passive: true });
      overlay.addEventListener('touchend', (event) => {
        const dx = event.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 60) show(index + (dx < 0 ? 1 : -1));
      }, { passive: true });

      document.addEventListener('keydown', (event) => {
        if (overlay.hidden) return;
        if (event.key === 'Escape') close();
        if (event.key === 'ArrowLeft') show(index - 1);
        if (event.key === 'ArrowRight') show(index + 1);
      });
    };

    images.forEach((img, i) => {
      img.style.cursor = 'zoom-in';
      img.addEventListener('click', () => {
        if (!overlay) build();
        overlay.hidden = false;
        void overlay.offsetWidth;
        overlay.classList.add('is-open');
        document.documentElement.classList.add('scroll-lock');
        show(i);
      });
    });
  };

  /* ---------- Init ---------- */
  const init = (scope = document) => {
    scope.querySelectorAll('[data-carousel]').forEach(initCarousel);
    scope.querySelectorAll('[data-before-after]').forEach(initBeforeAfter);
    initStickyOffer();
    initBadgeReveal(scope);
    initGalleryZoom(scope);
    Timer.render();
    Discount.show(scope);
  };

  setInterval(Timer.render, 1000);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init();
      Discount.init();
      enforceOnLoad();
    });
  } else {
    init();
    Discount.init();
    enforceOnLoad();
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  // Discount goes out too, so the product page can send a shopper straight to checkout
  // with the code they already earned still attached.
  window.Vinelora = { Cart, Drawer, Discount, formatMoney };
})();
